import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { f7 } from 'framework7-react';
import { useEffect, useState } from 'react';
import { useT } from '@/i18n';

// Build identity baked into the bundle at build time (see vite-env.d.ts +
// docker/dashboard build args). VITE_GIT_SHA tracks the actual running JS
// bundle - so it stays accurate even after a Capacitor OTA swap, which is
// exactly what QA needs to pin down "which build am I on".
const APP_NAME = '96Gastro';
const WEB_VERSION = import.meta.env.VITE_APP_VERSION ?? '0.0.0';
const GIT_SHA = import.meta.env.VITE_GIT_SHA ?? 'dev';
const BUILD_TIME = import.meta.env.VITE_BUILD_TIME;

function formatBuildDate(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Small, finished version label for the bottom of Settings (and anywhere QA /
 * customers need to report which build they're on). Tap copies the full
 * identity string to the clipboard so it can be pasted into a bug report.
 *
 * On native (iOS/Android) it shows the real installed app version + build
 * number - the same `1.0.0 (5)` you see in TestFlight / Play. On web it shows
 * the baked app version. Either way the git SHA pins the exact JS bundle.
 */
export function AppVersionFooter() {
  const t = useT();
  const [native, setNative] = useState<{ version: string; build: string } | null>(null);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cancelled = false;
    App.getInfo()
      .then((info) => {
        if (!cancelled) setNative({ version: info.version, build: info.build });
      })
      .catch(() => {
        /* getInfo unavailable - fall back to the baked web version */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const built = formatBuildDate(BUILD_TIME);
  const versionLine = native
    ? `${APP_NAME} v${native.version} (build ${native.build})`
    : `${APP_NAME} v${WEB_VERSION}`;
  // On native the version line is the immutable store shell version, so also
  // surface the web bundle version (bare, alongside the git SHA) to pin the
  // exact running content. Deliberately unlabelled - no self-update phrasing.
  const bundleTag = native ? WEB_VERSION : null;
  const metaLine = [bundleTag, GIT_SHA, built].filter(Boolean).join(' · ');
  const fullId = `${versionLine} · ${metaLine}`;

  const copy = () => {
    navigator.clipboard?.writeText(fullId).then(
      () => f7.toast.create({ text: t.versionCopied, closeTimeout: 1500 }).open(),
      () => {
        /* clipboard unavailable (older webview) - the label is still readable */
      },
    );
  };

  return (
    <button type="button" className="app-version-footer" onClick={copy} title={t.versionCopyHint}>
      <span className="app-version-footer__name">{versionLine}</span>
      <span className="app-version-footer__meta">{metaLine}</span>
    </button>
  );
}
